import { container } from 'inversify.config';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { RouteComponentProps } from 'react-router';
import { IdentityService } from 'services/identity-service';
import { OrderDefinition, Query } from 'stores/dataStore';
import { EventDto, EventsStore } from 'stores/events/events-store';
import { getProperties } from 'utils/object';
import { useTranslation } from 'react-i18next';
import EventListFilters from './event-list-filters';
import MobileListSpinner from '../shared/events-and-requests/mobile-list-spinner';
import MobileEventList from './mobile-events-list';
import MobileEventListFooter from '../shared/events-and-requests/mobile-list-footer';
import EventContainer from '../landing-pages/mobile/events/event-container';
import { extractFriendlyIdNumber, isNullOrWhiteSpaces } from 'utils/useful-functions';
// import PullToRefresh from '../shared/events-and-requests/pull-to-refresh';
import MobileEventWizard from './event-wizard/event-wizard-tw';

export interface MobileEventsContainerProps extends RouteComponentProps {
  eventsStore?: EventsStore;
  onEventClick?: (event: EventDto) => void;
}

const ITEMS_PER_PAGE = 10;

const MobileEventsContainer: React.FC<MobileEventsContainerProps> = ({ eventsStore, location, match, history, onEventClick }) => {
  const identityService: IdentityService = container.get(IdentityService);
  const { t } = useTranslation();

  const getEventStatusFilters = useCallback(() => {
    let activeFilters;
    const currentUserInfo = identityService.getUserInfo();

    if (IdentityService.isPoc(currentUserInfo)) {
      activeFilters = {
        or: [
          {
            and: [
              { EventStatus: 'Draft' },
              {
                or: [
                  { 'EventTypeItem/EventTypeConfiguration/RoleThatOwnEvents': 'PoC' },
                  "EventTypeItem/EventTypeConfiguration/RolesThatOwnEventsExtended/any(r:r eq 'PoC')"
                ]
              }
            ]
          },
          { EventStatus: 'Planned' },
          { EventStatus: 'InProgress' },
          { EventStatus: 'Completed' }
        ]
      };
    } else if (IdentityService.isPlannerMTC(currentUserInfo)) {
      activeFilters = { EventStatus: { eq: 'Draft' } };
    } else {
      activeFilters = {
        or: [{ EventStatus: 'Draft' }, { EventStatus: 'Planned' }, { EventStatus: 'InProgress' }, { EventStatus: 'Completed' }]
      };
    }

    return activeFilters;
  }, [identityService]);

  const [activeFilters, setActiveFilters] = useState<{ [key: string]: any }>({
    EventStatus: getEventStatusFilters()
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<EventDto>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isOnlySupportDetailsEnabled] = useState<boolean>(false);
  const [readOnlyForPocs] = useState<boolean>(true);
  const [createEvent, setCreateEvent] = useState<boolean>(false);

  let defaultOrder: OrderDefinition[] = [{ direction: 'Descending', field: 'friendlyEventId', useProfile: false }];

  const [events, setEvents] = useState<EventDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  const [eventFilters, setEventFilters] = useState<any[]>([]);

  const queryRef = useRef<Query>({
    searchQuery: '',
    orderBy: defaultOrder,
    take: ITEMS_PER_PAGE,
    skip: 0
  });

  const onNewItemClosed = async () => {
    setCreateEvent(false);
    await load(currentPage);
  };

  const load = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);
        const activeFiltersCopy = { ...activeFilters };

        const filter = [].concat(
          ...getProperties(activeFiltersCopy)
            .filter(({ value }) => !!value)
            .map(({ value }) => value)
        );

        const skip = (page - 1) * ITEMS_PER_PAGE;
        const queryCopy = { ...queryRef.current, filter, take: ITEMS_PER_PAGE, skip };
        const result = await eventsStore.getAllAsync(queryCopy);

        setEvents(result.items);
        setTotalItems(result.count || 0);
        queryRef.current = queryCopy;
        setError(null);
      } catch (err) {
        setError(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    },
    [eventsStore, activeFilters]
  );

  const handleOnBack = () => {
    const activeFiltersAux = {
      EventStatus: getEventStatusFilters()
    };
    setActiveFilters(activeFiltersAux);
    setEventFilters([]);
  };

  const handleOnCloseFilters = (value: boolean) => {
    const activeFiltersAux = {
      EventStatus: getEventStatusFilters()
    };
    setActiveFilters(activeFiltersAux);
    setEventFilters([]);
    if (value) setShowFilters(false);
  };

  useEffect(() => {
    const loadPage = async () => {
      await load(currentPage);
    };
    loadPage();
  }, [activeFilters, currentPage, load]);

  const handleFilterOnSave = (filter: any) => {
    const activeFiltersAux = {
      EventStatus: getEventStatusFilters()
    };

    if (filter.friendlyId) {
      activeFiltersAux['friendlyId'] = filter.friendlyId;

      if (isNullOrWhiteSpaces(filter.friendlyId)) {
        delete activeFiltersAux['friendlyId'];
      } else {
        const friendlyId = extractFriendlyIdNumber(filter.friendlyId, 'E');
        if (!Number.isNaN(friendlyId)) {
          activeFiltersAux['friendlyId'] = `contains(cast(FriendlyEventId, 'Edm.String'), '${friendlyId}')`;
        }
      }
    }
    if (filter.eventType) {
      // activeFiltersAux['eventType'] = filter.eventType;

      if (!filter.eventType || filter.eventType.length <= 0) {
        delete activeFiltersAux['eventType'];
      } else {
        const filterValue = {};
        filterValue['EventTypeItem/OriginalEventTypeId'] = { in: { type: 'guid', value: filter.eventType.map(x => x.id) } };
        activeFiltersAux['eventType'] = filterValue;
      }
    }
    if (filter.title) {
      if (isNullOrWhiteSpaces(filter.title)) delete activeFiltersAux['title'];
      else activeFiltersAux['title'] = { 'tolower(title)': { contains: filter.title.toLowerCase() } };
    }
    if (filter.category) {
      if (!filter.category) {
        delete activeFiltersAux['category'];
      } else {
        activeFiltersAux['category'] = { eventDetails: { category: { eq: filter.category } } };
      }
    }
    if (filter.machineRelated) {
      if (!filter.machineRelated || !filter.machineRelated.id) {
        delete activeFiltersAux['machineRelated'];
      } else {
        activeFiltersAux['machineRelated'] = {
          eventDetails: { isMachineRelated: { eq: filter.machineRelated.id === 'machine' ? true : false } }
        };
      }
    }
    if (filter.nmrCluster) {
      if (!filter.nmrCluster || filter.nmrCluster.length <= 0) {
        delete activeFiltersAux['nmrCluster'];
      } else {
        const filterValue = {};
        filterValue['eventDetails/nmrClusterId'] = { in: { value: filter.nmrCluster.map(cluster => cluster.id), type: 'guid' } };

        activeFiltersAux['nmrCluster'] = filterValue;
      }
    }

    if (filter.requests) {
      if (!filter.requests || filter.requests.length <= 0) {
        delete activeFiltersAux['requests'];
      } else {
        activeFiltersAux['requests'] = {
          requests: { any: [{ requestingLocationId: { in: { type: 'guid', value: filter.requests.map(location => location.id) } } }] }
        };
      }
    }
    if (filter.extendedRequests) {
      if (!filter.extendedRequests || filter.extendedRequests.length <= 0) {
        delete activeFiltersAux['extendedRequests'];
      } else {
        const filterValue = {};
        filterValue['extendedRequests'] = {
          any: [
            { Location: { regionId: { in: { value: filter.extendedRequests.map(locationRegion => locationRegion.id), type: 'guid' } } } }
          ]
        };
        activeFiltersAux['extendedRequests'] = filterValue;
      }
    }
    if (filter.eventLocation) {
      if (!filter.eventLocation || filter.eventLocation.length <= 0) {
        delete activeFiltersAux['eventLocation'];
      } else {
        const filterValue = {};
        filterValue['eventDetails/locationId'] = { in: { value: filter.eventLocation.map(location => location.id), type: 'guid' } };
        activeFiltersAux['eventLocation'] = filterValue;
      }
    }

    if (filter.delivery) {
      if (!filter.delivery.id) {
        delete activeFiltersAux['delivery'];
      } else
        activeFiltersAux['delivery'] = { eventTrainingDetails: { deliveryMethodId: { eq: { type: 'guid', value: filter.delivery.id } } } };
    }

    if (filter.startDate) {
      if (isNullOrWhiteSpaces(filter.startDate)) {
        delete activeFiltersAux['startDate'];
        setActiveFilters(activeFiltersAux);
      } else {
        activeFiltersAux['startDate'] = { startDate: { ge: new Date(filter.startDate) } };
      }
    }
    if (filter.endDate) {
      if (isNullOrWhiteSpaces(filter.endDate)) {
        delete activeFiltersAux['endDate'];
        setActiveFilters(activeFilters);
      } else {
        activeFiltersAux['endDate'] = { endDate: { le: new Date(filter.endDate) } };
        setActiveFilters(activeFiltersAux);
      }
    }
    if (filter.priority) {
      if (!filter.priority || filter.priority.length <= 0) {
        delete activeFiltersAux['priority'];
      } else {
        const filterValue = {};
        filterValue['eventDetails/priorityId'] = { in: { value: filter.priority.map(priority => priority.id), type: 'guid' } };
        activeFiltersAux['priority'] = filterValue;
      }
    }
    if (filter.instructors) {
      if (!filter.instructors || filter.instructors.length <= 0) {
        delete activeFiltersAux['instructors'];
      } else {
        activeFiltersAux['instructors'] = {
          eventInstructors: { any: [{ instructorId: { in: { type: 'guid', value: filter.instructors.map(location => location.id) } } }] }
        };
      }
    }
    if (filter.instructorsPillars) {
      if (!filter.instructorsPillars || filter.instructorsPillars.length <= 0) {
        delete activeFiltersAux['instructorsPillars'];
      } else {
        const filterValue = {};
        filterValue['instructorsPillars'] = {
          any: [{ Pillar: { Id: { in: { value: filter.instructorsPillars.map(instructorPillar => instructorPillar.id), type: 'guid' } } } }]
        };
        activeFiltersAux['instructorsPillars'] = filterValue;
      }
    }
    if (filter.profession) {
      if (!filter.profession || filter.profession.length <= 0) {
        delete activeFiltersAux['profession'];
      } else {
        const filterValue = {};
        filterValue['eventDetails/professionId'] = { in: { value: filter.profession.map(roles => roles.id), type: 'guid' } };
        activeFiltersAux['profession'] = filterValue;
      }
    }
    console.log('filter:', filter);
    if (filter.EventStatus) {
      console.log('filterStatus:', filter.EventStatus);
      if (!filter.EventStatus || filter.EventStatus.length <= 0) {
        delete activeFiltersAux['EventStatus'];
        activeFiltersAux['EventStatus'] = getEventStatusFilters();
      } else {
        const filterValue = {};

        filterValue['EventStatus'] = { in: filter.EventStatus };
        activeFiltersAux['EventStatus'] = filterValue;
      }
    }
    setShowFilters(false);
    setActiveFilters(activeFiltersAux);
    setEventFilters(filter);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: '#00b2a9',
          color: 'white',
          paddingTop: '1rem',
          paddingBottom: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          paddingLeft: '12px'
        }}
      >
        <h3 style={{ margin: 0 }}>{t('Events')}</h3>
      </div>
      <div
        ref={listRef}
        className="bg-white"
        style={{
          overflowY: 'visible',
          paddingBottom: '56px'
        }}
      >
        {createEvent ? (
          <MobileEventWizard onClose={() => onNewItemClosed()} />
        ) : showFilters ? (
          <EventListFilters onClose={value => handleOnCloseFilters(value)} onSave={e => handleFilterOnSave(e)} values={eventFilters} />
        ) : selectedItem ? (
          <EventContainer
            onBack={() => setSelectedItem(null)}
            onClose={() => setSelectedItem(null)}
            selectedEvent={selectedItem}
            mode="ViewDetails"
            fromRequestTab={false}
            isOnlySupportDetailsEnabled={isOnlySupportDetailsEnabled}
            readOnlyForPocs={readOnlyForPocs}
          />
        ) : (
          <>
            {loading ? (
              <MobileListSpinner />
            ) : error ? (
              <div className="p-4 text-red-600">{error}</div>
            ) : (
              <MobileEventList onEventClick={event => setSelectedItem(event)} events={events} onBack={handleOnBack} />
            )}
            {!loading && events.length > 0 && (
              <MobileEventListFooter
                currentPage={currentPage}
                onClick={setCurrentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                showActionFilters={true}
                setShowFilters={setShowFilters}
                entityType={'events'}
                onAddEvent={() => setCreateEvent(true)}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default MobileEventsContainer;
